import { of } from "@nozbe/watermelondb/utils/rx";
import { withObservables } from "@nozbe/watermelondb/react";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { CardStyleInterpolators } from "@react-navigation/stack";
import { useEffect } from "react";
import { View } from "react-native-ui-lib";
import { compose } from "recompose";
import { ModalCloseButton } from "@towersystems/roam-common-native";
import { CheckoutRootParamList } from "../../../../types";
import { moneyToFormattedString } from "../../../../utilities/money";
import { createStackHeaderNavigator } from "@towersystems/roam-common-native";
import { CheckoutController } from "../../components/CheckoutController";
import { PaymentController } from "../../components/PaymentController";
import { CheckoutContext } from "../../utils/checkout";
import {
  SaleControllerContextType,
  useSaleControllerProvider,
  withSaleController,
} from "../../utils/sale-controller";
import { CheckoutPaymentHistoryScreen } from "../CheckoutPaymentHistoryScreen";
import { SelectPaymentMethodScreen } from "../SelectPaymentMethodScreen";
import { SplitPaymentScreen } from "../SplitPaymentScreen";
import React from "react";
import { CheckoutSummaryRootScreen } from "../CheckoutSummaryRootScreen";
import { CreateSaleQuoteRootScreen } from "../CreateSaleQuoteRootScreen";
import { PaymentRootScreen } from "../PaymentRootScreen";
import { CheckoutLaybyRootScreen } from "../CheckoutLaybyRootScreen";
import { CheckoutInvoiceRootScreen } from "../CheckoutInvoiceRootScreen";
import { vendorCanAccessFeature } from "../../../../utilities/feature-flags";
import { AccountFeatureFlag } from "@towersystems/roam-common/lib/generated-types";
import { useCurrentVendorProvider } from "../../../../utilities/vendor";

// @do fix the types in this file with the HOCs

export type CheckoutRootScreenRendererProps = NativeStackScreenProps<
  any & CheckoutRootParamList,
  "CheckoutRootScreen"
> &
  SaleControllerContextType & {
    totals: any;
    balance: any;
  };

export const CheckoutRootScreenRenderer = ({
  totals,
  balance,
  navigation,
}: CheckoutRootScreenRendererProps) => {
  const [checkedCanClose, setCheckedCanClose] = React.useState<
    undefined | boolean
  >(undefined);
  const { currentVendor } = useCurrentVendorProvider();
  const { sale } = useSaleControllerProvider();

  const Stack = createStackHeaderNavigator<CheckoutRootParamList>();

  useEffect(() => {
    if (sale) {
      // dont automatically close quotes or invoices
      if (sale.isQuote || sale.isInvoice) {
        setCheckedCanClose(false);
        return;
      }

      if (sale.isClosed) {
        setCheckedCanClose(true);
        return;
      }

      sale.canTransitionToClosed_ASYNC().then((r) => {
        setCheckedCanClose(r);
      });
    } else {
      navigation.pop();
    }
  }, [sale]);

  if (checkedCanClose === undefined) {
    return <View />;
  }

  if (checkedCanClose === true) {
    return (
      <CheckoutController>
        <CheckoutContext.Consumer>
          {(context) => {
            if (!context) return <View></View>;
            return (
              <PaymentController checkoutController={context}>
                <Stack.Navigator>
                  <Stack.Screen
                    name="CheckoutSummaryRootScreen"
                    component={CheckoutSummaryRootScreen}
                    options={{
                      headerShown: false,
                      headerBackTitleVisible: false,
                      gestureEnabled: false,
                      animationEnabled: true,
                      cardStyleInterpolator:
                        CardStyleInterpolators.forHorizontalIOS,
                    }}
                  />
                </Stack.Navigator>
              </PaymentController>
            );
          }}
        </CheckoutContext.Consumer>
      </CheckoutController>
    );
  }

  return (
    <CheckoutController>
      <CheckoutContext.Consumer>
        {(context) => {
          if (!context) return <View></View>;
          return (
            <PaymentController checkoutController={context}>
              <Stack.Navigator
                screenOptions={{
                  headerShown: false,
                  title: totals
                    ? `Balance ${moneyToFormattedString(balance)}`
                    : "",
                }}
              >
                <Stack.Screen
                  name="SelectPaymentMethodScreen"
                  component={SelectPaymentMethodScreen}
                  options={{
                    headerShown: true,
                    headerLeft: () => <ModalCloseButton />,
                    animationEnabled: true,
                    cardStyleInterpolator:
                      CardStyleInterpolators.forHorizontalIOS,
                  }}
                />
                <Stack.Screen
                  name="SplitPaymentScreen"
                  component={SplitPaymentScreen}
                  options={{
                    headerShown: true,
                    headerLeft: () => <ModalCloseButton type="Back" />,
                    animationEnabled: true,
                    cardStyleInterpolator:
                      CardStyleInterpolators.forHorizontalIOS,
                  }}
                />
                <Stack.Screen
                  name="PaymentRootScreen"
                  component={PaymentRootScreen}
                  options={{
                    headerShown: false,
                    animationEnabled: true,
                    cardStyleInterpolator:
                      CardStyleInterpolators.forHorizontalIOS,
                  }}
                />
                <Stack.Screen
                  name="CheckoutSummaryRootScreen"
                  component={CheckoutSummaryRootScreen}
                  options={{
                    headerShown: false,
                    headerBackTitleVisible: false,
                    gestureEnabled: false,
                    animationEnabled: true,
                    cardStyleInterpolator:
                      CardStyleInterpolators.forHorizontalIOS,
                    headerLeft: () => <View />,
                  }}
                />
                {vendorCanAccessFeature(
                  AccountFeatureFlag.POS_APP_QUOTES_UI,
                  currentVendor
                ) && (
                  <Stack.Screen
                    name="CreateSaleQuoteRootScreen"
                    component={CreateSaleQuoteRootScreen}
                    options={{
                      headerShown: false,
                      gestureEnabled: false,
                      animationEnabled: true,
                      cardStyleInterpolator:
                        CardStyleInterpolators.forHorizontalIOS,
                    }}
                  />
                )}
                {vendorCanAccessFeature(
                  AccountFeatureFlag.POS_LAYBY_UI,
                  currentVendor
                ) && (
                  <Stack.Screen
                    name="CheckoutLaybyRootScreen"
                    component={CheckoutLaybyRootScreen}
                    options={{
                      headerShown: false,
                      gestureEnabled: false,
                      animationEnabled: true,
                      cardStyleInterpolator:
                        CardStyleInterpolators.forHorizontalIOS,
                    }}
                  />
                )}

                {vendorCanAccessFeature(
                  AccountFeatureFlag.POS_APP_INVOICES_UI,
                  currentVendor
                ) && (
                  <Stack.Screen
                    name="CheckoutInvoiceRootScreen"
                    component={CheckoutInvoiceRootScreen}
                    options={{
                      headerShown: false,
                      gestureEnabled: false,
                      animationEnabled: true,
                      cardStyleInterpolator:
                        CardStyleInterpolators.forHorizontalIOS,
                    }}
                  />
                )}

                <Stack.Group
                  screenOptions={{
                    presentation: "modal",
                    animationEnabled: true,
                    headerStyle: {
                      backgroundColor: "rgb(242, 242, 242)",
                    },
                  }}
                >
                  <Stack.Screen
                    name="CheckoutPaymentHistoryScreen"
                    component={CheckoutPaymentHistoryScreen}
                    options={{
                      headerShown: true,
                      headerLeft: () => <ModalCloseButton />,
                    }}
                  />
                </Stack.Group>
              </Stack.Navigator>
            </PaymentController>
          );
        }}
      </CheckoutContext.Consumer>
    </CheckoutController>
  );
};

const enhance = withObservables(["sale"], ({ sale }: any) => ({
  totals: sale ? sale.totals : of(null),
  balance: sale ? sale.balance : of(null),
}));

export const CheckoutRootScreen = compose(
  withSaleController,
  enhance
)(CheckoutRootScreenRenderer as any);
